/**
 * useGlobalFacility.ts
 *
 * Small helpers over useGlobalFacilityStore for facility-aware pages.
 *
 *   useEffectiveFacilityIds(local) - returns the global override list (as
 *                                    strings) when one is set, otherwise the
 *                                    caller's own local facility array. Use on
 *                                    list pages so the global selector silently
 *                                    wins over any per-page selection.
 *
 *   useEffectiveFacilityId(local)  - returns the FIRST override id if one is
 *                                    set, otherwise the caller's own local
 *                                    value. Use in single-facility contexts
 *                                    (modals, schedule, nav badges) that can
 *                                    only operate on one facility.
 *
 *   useIsFacilityLocked()          - true when an override is active. Pages
 *                                    should pass `['facility']` into the
 *                                    FilterSidebar's `lockedKeys` prop so the
 *                                    facility control renders disabled.
 */
import { useMemo } from 'react';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';

export function useEffectiveFacilityIds(pageValues?: string[]): string[] {
  const override = useGlobalFacilityStore((s) => s.selectedFacilityIds);
  return useMemo(
    () => (override.length > 0 ? override.map((id) => String(id)) : pageValues ?? []),
    [override, pageValues],
  );
}

export function useEffectiveFacilityId(pageValue?: number): number | undefined {
  const override = useGlobalFacilityStore((s) => s.selectedFacilityIds);
  if (override.length > 0) return override[0];
  return pageValue ?? undefined;
}

export function useIsFacilityLocked(): boolean {
  return useGlobalFacilityStore((s) => s.selectedFacilityIds.length > 0);
}
